import { Link } from 'react-router-dom';
import { Clock, Trophy, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 3600000;
const MS_PER_DAY = 86400000;

const DIFFICULTY_CONFIG = {
  Executive: { accent: '#c0c0c0', label: 'Executive', labelClass: 'text-gray-300 border-gray-400/30 bg-gray-400/10' },
  Signature: { accent: '#c0c0c0', label: 'Signature', labelClass: 'text-gray-300 border-gray-400/30 bg-gray-400/10' },
  Maison: { accent: '#d4af37', label: 'Maison', labelClass: 'text-amber-400 border-amber-400/30 bg-amber-400/10' },
  Discreet: { accent: '#e8e8e8', label: 'Discreet', labelClass: 'text-gray-200 border-gray-200/30 bg-white/5' },
};

const useCountdown = (endDate) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeLeft('Ended'); return; }
      const days = Math.floor(diff / MS_PER_DAY);
      const hours = Math.floor((diff % MS_PER_DAY) / MS_PER_HOUR);
      if (days > 0) setTimeLeft(`${days}d ${hours}h left`);
      else {
        const mins = Math.floor((diff % MS_PER_HOUR) / MS_PER_MINUTE);
        setTimeLeft(`${hours}h ${mins}m left`);
      }
    };
    calc();
    const timer = setInterval(calc, MS_PER_MINUTE);
    return () => clearInterval(timer);
  }, [endDate]);

  return timeLeft;
};

const API = process.env.REACT_APP_BACKEND_URL;

const ChallengeCard = ({ challenge }) => {
  const countdown = useCountdown(challenge.end_date);
  const diff = challenge.difficulty || 'Signature';
  const cfg = DIFFICULTY_CONFIG[diff] || DIFFICULTY_CONFIG.Signature;
  const num = challenge.number || '01';
  const [imgFailed, setImgFailed] = useState(false);

  // Resolve relative image paths
  const imgSrc = challenge.cover_image?.startsWith('/') ? `${API}${challenge.cover_image}` : challenge.cover_image;

  return (
    <Link
      to={`/challenges/${challenge.id}`}
      className="group block"
      data-testid={`challenge-card-${challenge.id}`}
    >
      <div className="relative overflow-hidden bg-[#111111] border border-white/5 hover:border-white/20 transition-all duration-700">
        {/* Number watermark */}
        <div className="absolute top-4 right-5 z-20 text-7xl font-serif italic opacity-[0.06] text-white pointer-events-none select-none">
          {num}
        </div>

        {/* Cover Image — grayscale to color */}
        <div className="relative h-56 overflow-hidden">
          {imgFailed ? (
            <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
              <span className="text-6xl font-serif italic text-white/5">{num}</span>
            </div>
          ) : (
            <img
              src={imgSrc}
              alt=""
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              onError={() => setImgFailed(true)}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-black/40 to-transparent" />

          {/* Active pill */}
          {challenge.status === 'active' && (
            <div className="absolute top-4 left-4 z-10">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 text-[10px] uppercase tracking-wider font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Active
              </span>
            </div>
          )}

          {/* Brief number */}
          <div className="absolute bottom-4 left-5 z-10">
            <span className="text-[10px] uppercase tracking-[0.3em] font-medium" style={{ color: cfg.accent }}>
              Brief {num}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Difficulty */}
          <span className={`inline-flex items-center px-2.5 py-1 border text-[10px] uppercase tracking-[0.2em] font-medium mb-4 ${cfg.labelClass}`}>
            {cfg.label}
          </span>

          <h3 className="text-xl font-serif italic text-white mb-3 group-hover:text-gray-200 transition-colors">
            {challenge.title}
          </h3>

          {/* Setting */}
          {challenge.setting && (
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-600 mb-3">{challenge.setting}</p>
          )}

          {/* Prompt */}
          <p className="text-sm text-gray-400 line-clamp-2 mb-5 leading-relaxed italic">
            {challenge.prompt || challenge.description}
          </p>

          {/* Prize */}
          <div className="flex items-center gap-2 mb-5 p-3 bg-[#0a0a0a] border border-white/5">
            <Trophy className="w-4 h-4 shrink-0" style={{ color: cfg.accent }} />
            <span className="text-xs text-gray-400 line-clamp-1">{challenge.prize_description}</span>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-600 pt-4 border-t border-white/5">
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              {challenge.entries_count} {challenge.entries_count === 1 ? 'entry' : 'entries'}
            </span>
            {challenge.status === 'active' && (
              <span className="flex items-center gap-1.5 text-emerald-400/80">
                <Clock className="w-3.5 h-3.5" />
                {countdown}
              </span>
            )}
          </div>
        </div>

        {/* Bottom accent line */}
        <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${cfg.accent}40, transparent)` }} />
      </div>
    </Link>
  );
};

export default ChallengeCard;
